<script setup lang="ts">
import { computed, useAttrs } from 'vue'
import { useHead } from '@unhead/vue'

import { ArticleType } from '@xcel-next/core'

import { useTeaser } from '../../../composables/use-teaser'
import { useImgSrcSet } from '../../../composables/use-img-src-set'
import { useLazyLoadingUtils } from '../../../composables/use-lazy-loading-utils'
import TrackingPixel from '../../../components/tracking/TrackingPixel.vue'
import TeaserPicture from '../../../components/teaser/TeaserPicture.vue'
import TeaserTopBadges from '../../../components/teaser/TeaserTopBadges.vue'
import TeaserMediaIcons from '../../../components/teaser/TeaserMediaIcons.vue'
import TeaserSliderControls from '../../../components/teaser/TeaserSliderControls.vue'
import Diets from '../../../components/paragraph-types/ParagraphTypeRecipe/Diets.vue'
import Icon from '../../../components/shared/Icon.vue'
import { formatNumber } from '../../../utils/numbers'
import { usePageStore } from '../../../stores/page'

import type { TeaserProps } from '../../../composables/use-teaser'

type Props = Omit<TeaserProps, 'composedTeaser'> & {
  composedTeaser?: TeaserProps['composedTeaser']
}

const props = withDefaults(defineProps<Props>(), {
  composedTeaser: () => ({}),
  metaData: () => ({}),
  includeDepth: 0,
  listOrderPosition: 0,
  isInlineTeaser: false,
  dataSliderInitialSlide: false,
  prioritizeImage: false
})

const {
  fallbackPlaceholderSource,
  showInlineTeaserSliderStyle,
  setBrandBadgeAlt,
  setLinkTarget,
  showDownloadIcon,
  showPlayIcon,
  showGalleryIcon,
  showAdBadge,
  showOwnerBrand,
  imageIsAiGenerated,
  setStyleClasses,
  headline,
  imageAltText,
  copytext,
  imageUrl,
  imageFetchPriority,
  headlineTagType,
  shouldAddDataNoSnippet,
  isFavoriteTeaser,
  cdnImageUrl,
  getImageOverlayStyleClass,
  imgSrcSetArray,
  brandBadgeSrc,
  addTeaserImagePreloadLinksToHead
} = useTeaser(props)

const { createImgSrc, getImageAltText } = useImgSrcSet()
const { getLoadingValue } = useLazyLoadingUtils(props)

useHead(computed(() => addTeaserImagePreloadLinksToHead()))

const pageStore = usePageStore()
const attrs = useAttrs()
const recipeOfTheDayDate = props.composedTeaser.usages?.find(({ usage }) => usage?.publicationTags?.includes('recipeoftheday') && usage?.publicationDate)?.usage?.publicationDate

const averageString = computed(() => formatNumber(props.composedTeaser.ratingAverage, 1))
const isBrandedRecipe = computed(() => props.composedTeaser.isBrandedRecipe && props.composedTeaser.presenter)
const presenterImageUrl = computed(() => {
  const presenter = props.composedTeaser.presenter
  if (!isBrandedRecipe.value || !presenter) return undefined
  const { width, height } = presenter
  const ratio = width && height ? width / height : 1
  return createImgSrc({
    image: presenter,
    brand: pageStore.brandFromStore,
    type: 'webp',
    width: Math.ceil(ratio * 45)
  }) ?? undefined
})
const isSliderChild = computed(() => attrs['data-is-slider-child'])
const sliderTeaserBadgeText = computed(() => props.composedTeaser.articleType === ArticleType.recipe ? 'Rezept' : 'Artikel')
</script>

<template>
  <section :class="setStyleClasses">
    <div v-if="composedTeaser.externalCssClass"
         :class="composedTeaser.externalCssClass" />
    <a v-else
       :href="composedTeaser.url"
       :rel="composedTeaser.rel"
       :target="setLinkTarget"
       data-tc="teaser">
      <figure v-if="imageUrl"
              class="bx-teaser__image">
        <div v-if="composedTeaser.overlayImageUrl && composedTeaser.overlayImageUrl.length > 0"
             :class="getImageOverlayStyleClass(composedTeaser)" />
        <TeaserPicture :src-set-array="imgSrcSetArray('webp')"
                       :fallback-placeholder-source="fallbackPlaceholderSource"
                       :loading="getLoadingValue"
                       :fetch-priority="imageFetchPriority"
                       :alt-text="getImageAltText(composedTeaser, imageAltText)" />
        <TeaserTopBadges :show-owner-brand="showOwnerBrand"
                         :show-ad-badge="showAdBadge"
                         :image-is-ai-generated="imageIsAiGenerated"
                         :is-affiliate="composedTeaser?.affiliate"
                         :brand-badge-alt="setBrandBadgeAlt"
                         :brand-badge-src="brandBadgeSrc" />
        <div v-if="showPlayIcon || showDownloadIcon || showGalleryIcon || isSliderChild"
             class="bx-badges bx-badges--bottom bx-badges--over-image">
          <TeaserMediaIcons :show-play-icon="showPlayIcon"
                            :show-download-icon="showDownloadIcon"
                            :show-gallery-icon="showGalleryIcon" />
          <div v-if="isSliderChild"
               class="bx-badge bx-badge--slider-child-teaser">
            Zum {{ sliderTeaserBadgeText }}
          </div>
        </div>

        <template v-if="showInlineTeaserSliderStyle">
          <TeaserSliderControls :num-slides="composedTeaser.numSlides"
                                :slider-contents="composedTeaser.sliderContents" />
        </template>
        <div v-if="recipeOfTheDayDate"
             class="bx-badge bx-badge--recipeoftheday bx-badge--over-image">
          <svg class="icon icon-recipeoftheday">
            <use :href="`#${new Date(recipeOfTheDayDate).toLocaleString('de-de', { weekday: 'short' }).toLowerCase()}`" />
          </svg>
        </div>
      </figure>
      <figure v-else-if="metaData.isSliderPreview"
              class="bx-teaser__image">
        <div v-if="composedTeaser.overlayImageUrl && composedTeaser.overlayImageUrl.length > 0"
             :class="getImageOverlayStyleClass(composedTeaser)" />
        <img v-if="metaData.isLoading"
             :src="metaData.imgUrlLoading">
        <img v-else-if="metaData.error"
             :src="metaData.imgUrlError">
        <img v-else-if="metaData.errorNotEnoughContent"
             :src="metaData.imgUrlErrorNotEnoughContent">
        <img v-else
             :src="cdnImageUrl('/images/default-placeholder.jpg')">
      </figure>

      <div class="bx-teaser__content"
           data-tc="teaser-content">
        <div v-if="showPlayIcon || showDownloadIcon || showGalleryIcon"
             class="bx-badges bx-badges--bottom bx-badges--over-content">
          <TeaserMediaIcons :show-play-icon="showPlayIcon"
                            :show-download-icon="showDownloadIcon"
                            :show-gallery-icon="showGalleryIcon"
                            variant="content" />
        </div>
        <div v-if="isFavoriteTeaser"
             class="bx-badge bx-badge--favorite">
          LECKER Favorit
        </div>
        <div class="bx-badges bx-badges--top bx-badges--over-content">
          <span v-if="showAdBadge"
                class="bx-badge bx-badge--ad"
                :class="{ 'bx-badge--affiliate-link': composedTeaser?.affiliate }" />
        </div>
        <div v-if="composedTeaser.categoryName"
             class="bx-teaser__category"
             data-tc="teaser-category">
          {{ composedTeaser.categoryName }}
        </div>
        <div v-if="composedTeaser.roofline || recipeOfTheDayDate"
             class="bx-teaser__roofline"
             :data-nosnippet="shouldAddDataNoSnippet"
             data-tc="teaser-roofline">
          {{ composedTeaser.roofline || new Date(recipeOfTheDayDate!).toLocaleDateString('de-de', { day: '2-digit', month: 'long', year: 'numeric' }) }}
        </div>
        <div>
          <component :is="headlineTagType"
                     v-if="headline"
                     :data-nosnippet="shouldAddDataNoSnippet"
                     class="bx-teaser__headline"
                     data-tc="teaser-headline">
            <div v-if="isBrandedRecipe"
                 class="bx-teaser__presenter"
                 :data-nosnippet="shouldAddDataNoSnippet"
                 data-tc="teaser-presenter">
              <figure>
                <img :src="presenterImageUrl">
              </figure>
            </div>
            {{ headline }}
          </component>

          <p v-if="copytext"
             :data-nosnippet="shouldAddDataNoSnippet"
             class="bx-teaser__copy">
            {{ copytext }}
          </p>

          <ul v-if="isFavoriteTeaser"
              class="bx-teaser__advantages">
            <li v-for="advantage in composedTeaser.advantages"
                :key="advantage">
              <Icon name="check"
                    class="icon-check" />
              <span>{{ advantage }}</span>
            </li>
          </ul>

          <div v-if="showPlayIcon"
               class="bx-badges bx-badges--below-teaser-content">
            <svg class="bx-badge bx-badge--video icon icon-play">
              <use href="#play" />
            </svg>
          </div>
        </div>
        <div v-if="(composedTeaser.cookingTime ?? 0) > 0 || composedTeaser.dietaryConsiderations || composedTeaser.ratingAverage"
             :data-nosnippet="shouldAddDataNoSnippet"
             class="bx-teaser__meta">
          <div class="bx-teaser-meta__wrapper">
            <div v-if="composedTeaser.ratingAverage"
                 class="bx-teaser-meta__rating">
              <Icon name="star"
                    class="icon-star--100" />
              {{ averageString }}
            </div>
            <div v-if="(composedTeaser.cookingTime ?? 0) > 0"
                 class="bx-teaser-meta__cookingtime">
              <Icon name="clock" />
              {{ composedTeaser.cookingTime }}&nbsp;Min.
            </div>
          </div>
          <Diets v-if="composedTeaser.dietaryConsiderations"
                 :diets="composedTeaser.dietaryConsiderations" />
        </div>
      </div>
      <TrackingPixel v-if="composedTeaser.trackingPixel"
                     :tracking-pixel="composedTeaser.trackingPixel" />
    </a>
  </section>
</template>
